import React, { useState, useEffect, useRef } from 'react';

// Game constants
const CANVAS_WIDTH = 1200;
const CANVAS_HEIGHT = 600;
const CANDLE_WIDTH = 14;
const CANDLE_SPACING = 5;
const AXIS_PADDING = 96;
const RIGHT_MARGIN = 36;
const USABLE_WIDTH = CANVAS_WIDTH - AXIS_PADDING - RIGHT_MARGIN;
const MAX_CANDLES = Math.floor(USABLE_WIDTH / (CANDLE_WIDTH + CANDLE_SPACING));
const TOTAL_SUPPLY = 1_000_000_000; // 1 Billion tokens
const INITIAL_TOKEN_PRICE = 0.000055; // $0.000055 per token
const INITIAL_MARKET_CAP = INITIAL_TOKEN_PRICE * TOTAL_SUPPLY; // $55,000

function CryptoCrashGame({ onClose }) {
  const [gameState, setGameState] = useState('waiting');
  const [currentMultiplier, setCurrentMultiplier] = useState(1.00);
  const [bet, setBet] = useState(100);
  const [tokens, setTokens] = useState(1000);
  
  const canvasRef = useRef(null);
  const gameStateRef = useRef('waiting');
  const candleDataRef = useRef([]);
  const multiplierRef = useRef(1.00);
  const gameLoopRef = useRef(null);
  const audioRef = useRef(null);

  function drawGrid(ctx) {
    ctx.strokeStyle = '#2a2a2a';
    ctx.lineWidth = 1;
    
    // Calculate price ranges for the y-axis based on current market cap
    const currentMarketCap = candleDataRef.current.length > 0
      ? candleDataRef.current[candleDataRef.current.length - 1].close * TOTAL_SUPPLY
      : INITIAL_MARKET_CAP;
    
    const maxMarketCap = currentMarketCap * 1.5;
    const minMarketCap = INITIAL_MARKET_CAP * 0.5;
    const stepCount = 5;
    const stepSize = (maxMarketCap - minMarketCap) / stepCount;
    
    // Draw horizontal grid lines and labels
    for (let i = 0; i <= stepCount; i++) {
      const y = CANVAS_HEIGHT - (i * (CANVAS_HEIGHT / stepCount));
      
      // Draw grid line
      ctx.beginPath();
      ctx.moveTo(AXIS_PADDING, y);
      ctx.lineTo(CANVAS_WIDTH, y);
      ctx.stroke();
      
      // Calculate and format market cap for this line
      const marketCap = minMarketCap + (i * stepSize);
      const marketCapFormatted = marketCap.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      
      // Draw market cap label
      ctx.fillStyle = '#888888';
      ctx.font = '12px Arial';
      ctx.textAlign = 'right';
      ctx.fillText(marketCapFormatted, AXIS_PADDING - 5, y + 4);
    }
    
    // Draw vertical grid lines
    for (let i = AXIS_PADDING; i <= CANVAS_WIDTH; i += 50) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, CANVAS_HEIGHT);
      ctx.stroke();
    }
  }

  function drawCandle(ctx, candle, index, minPrice, maxPrice, isRugPull = false) {
    // Position from left edge with padding, ensuring candles stay within usable area
    const x = AXIS_PADDING + ((USABLE_WIDTH / MAX_CANDLES) * index);
    
    // Price to Y coordinate conversion with padding
    const normalizePrice = (price) => {
      const padding = 20;
      const priceRange = maxPrice - minPrice;
      const pixelRange = CANVAS_HEIGHT - (padding * 2);
      const normalized = ((price - minPrice) / priceRange) * pixelRange;
      return CANVAS_HEIGHT - padding - normalized;
    };

    // Special handling for rug pull price normalization
    const normalizeRugPullPrice = (price) => {
      if (price === 0) {
        return CANVAS_HEIGHT * 4; // Extend way beyond visible area
      }
      return normalizePrice(price);
    };
    
    // Use appropriate price normalization based on candle type
    const getPriceY = isRugPull ? normalizeRugPullPrice : normalizePrice;
    
    const y_open = getPriceY(candle.open);
    const y_close = getPriceY(candle.close);
    const y_high = getPriceY(candle.high);
    const y_low = getPriceY(candle.low);
    
    // Draw wick
    ctx.beginPath();
    ctx.strokeStyle = candle.close >= candle.open ? '#00ff00' : '#ff0000';
    ctx.lineWidth = 2;
    ctx.moveTo(x + (CANDLE_WIDTH / 2), y_high);
    ctx.lineTo(x + (CANDLE_WIDTH / 2), y_low);
    ctx.stroke();
    
    // Draw body
    ctx.fillStyle = candle.close >= candle.open ? '#00ff00' : '#ff0000';
    const bodyStart = Math.min(y_open, y_close);
    const bodyHeight = Math.max(2, Math.abs(y_close - y_open));
    ctx.fillRect(x, bodyStart, CANDLE_WIDTH, bodyHeight);
  }

  
  function generateNewCandle(lastPrice) {
    const trend = Math.random() > 0.4; // 60% up, 40% down (was 50-50)
    const isAggressiveMove = Math.random() < 0.2; // Reduced from 25% to 20%
    
    const baseVolatility = 0.2; // Reduced from 0.25
    const aggressiveVolatility = 0.35; // Reduced from 0.4
    const volatility = isAggressiveMove ? aggressiveVolatility : baseVolatility;
    
    let movePercent;
    if (isAggressiveMove) {
      movePercent = (Math.random() * volatility) * (trend ? 1 : -1.5); // Less harsh drops
    } else {
      movePercent = (Math.random() * volatility) * (trend ? 0.9 : -0.8); // More balanced
    }
    
    const newPrice = lastPrice * (1 + movePercent);
    const wickMultiplier = isAggressiveMove ? 1.5 : 1.1; // Less extreme wicks
    const high = Math.max(lastPrice, newPrice) * (1 + Math.random() * 0.05 * wickMultiplier);
    const low = Math.min(lastPrice, newPrice) * (1 - Math.random() * 0.05 * wickMultiplier);
    
    return {
      open: lastPrice,
      close: newPrice,
      high: high,
      low: low,
      timestamp: Date.now()
    };
  }
  
  function updateChart() {
    if (gameStateRef.current !== 'playing') return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    drawGrid(ctx);
    
    const lastCandle = candleDataRef.current[candleDataRef.current.length - 1];
    const newCandle = generateNewCandle(lastCandle.close);
    
    candleDataRef.current = [...candleDataRef.current, newCandle].slice(-MAX_CANDLES);
    
    const prices = candleDataRef.current.flatMap(c => [c.high, c.low]);
    const minPrice = Math.min(...prices) * 0.95;
    const maxPrice = Math.max(...prices) * 1.05;
    
    candleDataRef.current.forEach((candle, index) => {
      drawCandle(ctx, candle, index, minPrice, maxPrice, false);
    });
    
    multiplierRef.current = (newCandle.close / INITIAL_TOKEN_PRICE).toFixed(2);
    setCurrentMultiplier(multiplierRef.current);
    
    if (Math.random() < 0.015) {
      crashGame();
    }
  }

  function crashGame() {
    gameStateRef.current = 'crashed';
    setGameState('crashed');

    // Play crash sound
    audioRef.current = new Audio('/audio/sfx/adi.mp3');
    audioRef.current.volume = 0.8; // 50% volume
    audioRef.current.play().catch(error => console.error('Audio playback failed:', error));

    if (gameLoopRef.current) {
      clearInterval(gameLoopRef.current);
      gameLoopRef.current = null;
    }
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const lastCandle = candleDataRef.current[candleDataRef.current.length - 1];
    
    const crashCandle = {
      open: lastCandle.close,
      close: 0,
      high: lastCandle.close,
      low: 0,
      timestamp: Date.now()
    };
    
    const updatedData = [...candleDataRef.current, crashCandle].slice(-MAX_CANDLES);
    candleDataRef.current = updatedData;
    
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    drawGrid(ctx);
    
    const prices = updatedData.flatMap(c => [c.high, c.low]);
    const minPrice = Math.min(...prices.filter(p => p > 0)) * 0.95;
    const maxPrice = Math.max(...prices) * 1.05;
    
    updatedData.slice(0, -1).forEach((candle, index) => {
      drawCandle(ctx, candle, index, minPrice, maxPrice, false);
    });
    
    const lastIndex = updatedData.length - 1;
    drawCandle(ctx, crashCandle, lastIndex, minPrice, maxPrice, true);
    
  }

  function startGame() {
    if (tokens < bet) return;
    
    if (gameLoopRef.current) {
      clearInterval(gameLoopRef.current);
      gameLoopRef.current = null;
    }
    
    setTokens(prev => Math.round(prev - bet));
    setGameState('playing');
    gameStateRef.current = 'playing';
    
    const initialCandle = {
      open: INITIAL_TOKEN_PRICE,
      high: INITIAL_TOKEN_PRICE,
      low: INITIAL_TOKEN_PRICE,
      close: INITIAL_TOKEN_PRICE,
      timestamp: Date.now()
    };
    
    candleDataRef.current = [initialCandle];
    multiplierRef.current = 1.00;
    setCurrentMultiplier(1.00);
    
    gameLoopRef.current = setInterval(updateChart, 115);
  }

  function cashOut() {
    if (gameStateRef.current !== 'playing') return;
    const winnings = Math.round(bet * multiplierRef.current);
    setTokens(prev => Math.round(prev + winnings));
    setGameState('waiting');
    gameStateRef.current = 'waiting';
    if (winnings > bet) {
        audioRef.current = new Audio('/audio/sfx/win.mp3');
        audioRef.current.volume = 1.0; // 100% volume
        audioRef.current.play().catch(error => console.error('Audio playback failed:', error));
    } else {
        audioRef.current = new Audio('/audio/sfx/lose.mp3');
        audioRef.current.volume = 1.0; // 100% volume
        audioRef.current.play().catch(error => console.error('Audio playback failed:', error));
    }


    if (gameLoopRef.current) {
      clearInterval(gameLoopRef.current);
      gameLoopRef.current = null;
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    
    const ctx = canvas.getContext('2d');
    drawGrid(ctx);
    
    return () => {
      if (window.gameLoopInterval) {
        clearInterval(window.gameLoopInterval);
      }
    };
  }, []);


  return (
    <div style={{ 
      width: '100%', 
      maxWidth: `${CANVAS_WIDTH}px`,
      padding: '2px',
      backgroundColor: '#c0c0c0',
      border: 'outset 2px #fff',
      fontFamily: 'Microsoft Sans Serif, Arial',
      color: '#FFF'
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '4px',
          backgroundColor: '#00007C',
          border: 'inset 2px #808080'
        }}>
          <div className="stats" style={{ display: 'flex', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#fff' }}>Ticker</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                $GOTCHI
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#fff' }}>Token Price</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                ${(currentMultiplier * INITIAL_TOKEN_PRICE).toFixed(6)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#fff' }}>Market Cap</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                {gameState === 'crashed' ? '$0' : (currentMultiplier * INITIAL_MARKET_CAP).toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#fff' }}>Multiplier</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#84CC16' }}>
                {currentMultiplier}x
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#fff' }}>Tokens</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{tokens}</div>
            </div>
            <div>
              <button
                style={{
                  padding: 0,
                  paddingTop: 6,
                  paddingRight: 4,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseDown={(e) => {
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.src = '/sprites/ui/Windows_Button_Pressed_X.png';
                }}
                onMouseUp={(e) => {
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.src = '/sprites/ui/Windows_Button_X.png';
                  onClose();
                }}
                onMouseLeave={(e) => {
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.src = '/sprites/ui/Windows_Button_X.png';
                }}
                onTouchStart={(e) => {
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.src = '/sprites/ui/Windows_Button_Pressed_X.png';
                }}
                onTouchEnd={(e) => {
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.src = '/sprites/ui/Windows_Button_X.png';
                  onClose();
                }}
              >
                <img
                  src="/sprites/ui/Windows_Button_X.png"
                  alt="X button"
                  style={{ width: '24px', height: '24px' }}
                />
              </button>
            </div>
          </div>
        </div>
        
        <canvas
        ref={canvasRef}
        style={{
          width: `${CANVAS_WIDTH}px`,
          height: `${CANVAS_HEIGHT}px`,
          backgroundColor: '#000',
          border: 'inset 2px #808080'
        }}
      />
        
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', padding: '4px' }}>
            <span className="text-black">
            Buy:

            </span>
          <input
            type="number"
            value={bet}
            onChange={(e) => setBet(Math.max(0, parseInt(e.target.value)))}
            style={{
              backgroundColor: '#fff',
              padding: '2px',
              width: '8rem',
              color: '#000',
              border: 'inset 2px #808080',
              fontFamily: 'Microsoft Sans Serif, Arial'
            }}
            disabled={gameState === 'playing'}
          />
            
          {(gameState === 'crashed' || gameState === 'waiting') && (
            <button
              onClick={startGame}
              style={{
                backgroundColor: '#c0c0c0',
                padding: '4px 8px',
                border: 'outset 2px #fff',
                color: '#000',
                fontFamily: 'Microsoft Sans Serif, Arial',
                cursor: tokens >= bet ? 'pointer' : 'not-allowed',
                opacity: tokens >= bet ? 1 : 0.5,
              }}
              disabled={tokens < bet}
            >
              Start Game
            </button>
          )}
            
          {gameState === 'playing' && (
            <button
              onClick={cashOut}
              style={{
                backgroundColor: '#c0c0c0',
                padding: '4px 8px',
                border: 'outset 2px #fff',
                color: '#000',
                fontFamily: 'Microsoft Sans Serif, Arial',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Cash Out
            </button>
          )}
            
          {gameState === 'crashed' && (
            <div style={{ 
              color: '#ff0000', 
              fontSize: '20px',
              fontWeight: 'bold',
              fontFamily: 'Microsoft Sans Serif, Arial'
            }}>
              Rug Pulled! 💀
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CryptoCrashGame;